import React, { useEffect, createRef, useCallback } from 'react'
import { motion } from 'framer-motion'
import styles from './Modal.module.scss'
import Portal from '../Portal/Portal'

const Modal = ({ children, onModalClose }) => {
  // Element that was focused when opening the modal
  const activeElement = document.activeElement
  const modalRef = createRef()

  const handleTabKey = useCallback(
    (e) => {
      const focusableModalElements = modalRef.current.querySelectorAll(
        'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], input[type="submit"], select'
      )
      const firstElement = focusableModalElements[0]
      const lastElement =
        focusableModalElements[focusableModalElements.length - 1]

      if (!e.shiftKey && document.activeElement === lastElement) {
        firstElement.focus()
        return e.preventDefault()
      }

      if (e.shiftKey && document.activeElement === firstElement) {
        lastElement.focus()
        e.preventDefault()
      }
    },
    [modalRef]
  )

  useEffect(() => {
    const keyListenersMap = new Map([
      [27, onModalClose],
      [9, handleTabKey],
    ])

    const keyListener = (e) => {
      const listener = keyListenersMap.get(e.keyCode)
      return listener && listener(e)
    }
    document.addEventListener('keydown', keyListener)
    return () => {
      document.removeEventListener('keydown', keyListener)
      // Return focus to the previously focused element
      activeElement.focus()
    }
  }, [activeElement, handleTabKey, onModalClose])

  function onClickOutside(e) {
    if (e.target.getAttribute('data-click') === 'outside') {
      onModalClose()
    }
  }

  const backdrop = {
    visible: { opacity: 1 },
    hidden: { opacity: 0 },
  }

  const modal = {
    hidden: { translateY: '-100%', opacity: 0 },
    visible: {
      translateY: 0,
      opacity: 1,
      transition: { delay: 0.3 },
    },
  }

  return (
    <>
      <motion.div
        variants={backdrop}
        initial="hidden"
        animate="visible"
        exit="hidden"
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        onClick={onClickOutside}
        data-click="outside"
      >
        <motion.div variants={modal} className={styles.content} ref={modalRef}>
          {children}
        </motion.div>
      </motion.div>
    </>
  )
}
export default Portal(Modal)
